
import React, { useState, useEffect } from 'react';
import { Box, List, ListItemText, Checkbox, TableBody, TableCell, Table, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  MainContainer,
  DescriptionText,
  LayoutGrid,
  FacilityTypesPanel,
  FacilityTypesTitle,
  FacilityTypeItem,
  MappingPanel,
  MappingTitle,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  SaveButton
} from './styles';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import {
  searchFacilityTypes,
  searchDocumentTypes,
  createFacilityTypeDocumentMapping,
  getFacilityTypeDocumentMapping
} from '../../../modules/masterdata/actions';
import {
  selectFacilityTypesList,
  selectDocumentTypesList,
  selectFacilityTypeDocumentMappingData,
  selectCreateFacilityTypeDocumentMappingSuccess,
  selectCreateFacilityTypeDocumentMappingLoading,
  selectFacilityTypeDocumentMappingLoading,
  selectCreateFacilityTypeDocumentMappingError,
  selectCreateFacilityTypeDocumentMappingMessage
} from '../../../modules/masterdata/selectors';
import { AppDispatch } from '../../../store';
import { resetCreateFacilityTypeDocumentMappingState } from '../../../modules/masterdata/reducers';
import AlertPopup from '../../../components/common/AlertPopup';

const FacilityTypeDocumentTypeMapping: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  // Redux Selectors
  // Note: I need to verify selectFacilityTypesList exists. If not, I'll use the one for searchFacilityTypes
  const facilityTypes = useSelector(selectFacilityTypesList);
  const documentTypes = useSelector(selectDocumentTypesList);
  const currentMapping = useSelector(selectFacilityTypeDocumentMappingData);
  const isSaving = useSelector(selectCreateFacilityTypeDocumentMappingLoading);
  const saveSuccess = useSelector(selectCreateFacilityTypeDocumentMappingSuccess);
  const saveError = useSelector(selectCreateFacilityTypeDocumentMappingError);
  const saveMessage = useSelector(selectCreateFacilityTypeDocumentMappingMessage);
  const isLoadingMapping = useSelector(selectFacilityTypeDocumentMappingLoading);


  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<number[]>([]);
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setActivePage('facility-type-doc-mapping');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Facility Type Document Mapping', path: '/master-data/facility-type-doc-mapping' },
    ]);
    setIsShow(true);

    // Initial Fetch
    dispatch(searchFacilityTypes({ search: '', page: 1, page_size: 100 }));
    dispatch(searchDocumentTypes({ search: '', page: 1, page_size: 100 }));

    return () => {
      dispatch(resetCreateFacilityTypeDocumentMappingState());
    }
  }, [setActivePage, setPath, setIsShow, dispatch]);


  // Set default selected type when types are loaded
  useEffect(() => {
    if (facilityTypes.length > 0 && selectedTypeId === null) {
      setSelectedTypeId(facilityTypes[0].facility_type_id);
    }
  }, [facilityTypes, selectedTypeId]);

  // Fetch mapping when selected type changes
  useEffect(() => {
    if (selectedTypeId) {
      dispatch(getFacilityTypeDocumentMapping(selectedTypeId));
    }
  }, [selectedTypeId, dispatch]);

  // Update local state when mapping data comes from Redux
  useEffect(() => {
    if (currentMapping) {
      // Check if the mapping corresponds to the currently selected type (loose equality for safety)
      // eslint-disable-next-line eqeqeq
      if (currentMapping.facility_type_id == selectedTypeId) {
        const docTypes = currentMapping.document_types || [];
        // Handle potential object vs ID inconsistency
        const ids = docTypes.map((d: any) => {
          if (typeof d === 'object' && d !== null) {
            return d.document_id || d.id;
          }
          return d;
        });
        setSelectedDocIds(ids);
      }
    }
  }, [currentMapping, selectedTypeId]);


  const handleTypeClick = (id: number) => {
    setSelectedTypeId(id);
    setSelectedDocIds([]); // Clear while loading new ones
  };

  const handleToggleDoc = (docId: number) => {
    setSelectedDocIds(prev => {
      const isSelected = prev.includes(docId);
      if (isSelected) {
        return prev.filter(id => id !== docId);
      } else {
        return [...prev, docId];
      }
    });
  };

  const handleSave = () => {
    if (selectedTypeId) {
      dispatch(createFacilityTypeDocumentMapping({
        facility_type_id: selectedTypeId,
        document_types: selectedDocIds
      }));
    }
  };

  useEffect(() => {
    if (saveSuccess) {
      setAlert({ message: saveMessage || 'Facility Type document mapping updated successfully', severity: 'success' });
      dispatch(resetCreateFacilityTypeDocumentMappingState());
      if (selectedTypeId) dispatch(getFacilityTypeDocumentMapping(selectedTypeId));
    }
    if (saveError) {
      setAlert({ message: saveError || 'Failed to update mapping', severity: 'error' });
      dispatch(resetCreateFacilityTypeDocumentMappingState());
    }
  }, [saveSuccess, saveError, saveMessage, dispatch, selectedTypeId]);

  return (
    <MainContainer>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <DescriptionText>
        Set upload rules by selecting a facility type and assigning the required documents.
      </DescriptionText>

      <LayoutGrid>
        {/* Left Panel: Facility Types List */}
        <FacilityTypesPanel>
          <FacilityTypesTitle>Facility Types</FacilityTypesTitle>
          <List component="nav" disablePadding>
            {facilityTypes.map((type) => (
              <FacilityTypeItem
                key={type.facility_type_id}
                selected={selectedTypeId === type.facility_type_id}
                onClick={() => handleTypeClick(type.facility_type_id)}
              >
                <ListItemText primary={type.facility_type_name} />
              </FacilityTypeItem>
            ))}
          </List>
        </FacilityTypesPanel>

        {/* Right Panel: Document Mapping */}
        <MappingPanel>
          <MappingTitle>
            Document Types Required
            {isLoadingMapping && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </MappingTitle>

          <StyledTableContainer>
            <Table>
              <StyledTableHead>
                <StyledTableRow>
                  {/* Checkbox column header is empty or could have 'Select All' */}
                  <TableCell padding="checkbox" />
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                </StyledTableRow>
              </StyledTableHead>
              <TableBody>
                {documentTypes.map((doc) => {
                  const isChecked = selectedDocIds.includes(doc.document_id);
                  return (
                    <StyledTableRow key={doc.document_id} onClick={() => handleToggleDoc(doc.document_id)} role="checkbox" sx={{ cursor: 'pointer' }}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={isChecked} color="primary" />
                      </TableCell>
                      <TableCell>{doc.document_type}</TableCell>
                      <TableCell sx={{ color: '#666' }}>{doc.document_description}</TableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>

          <Box sx={{ display: 'flow-root' }}> {/* Clear floats */}
            <SaveButton
              variant="contained"
              disableElevation
              onClick={handleSave}
              disabled={isSaving || !selectedTypeId}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </SaveButton>
          </Box>
        </MappingPanel>
      </LayoutGrid>
    </MainContainer>
  );
};

export default FacilityTypeDocumentTypeMapping;
